import {
  AdminColorInput,
  AdminDollarInput2,
  AdminSelect,
  AdminSwitch,
  Box,
  Button,
  CreateCustomExpenseDto,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  Label,
  lightTheme,
  ModalPortal,
  Tab,
} from '@vgrubs/components';
import { useState } from 'react';
import { ExpenseNameField } from './ExpenseNameField';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const YEARS = (() => {
  const now = new Date().getFullYear();
  const list: number[] = [];
  for (let y = 2020; y <= now + 5; y++) list.push(y);
  return list;
})();

const MonthOptions = (
  <>
    {MONTHS.map((m, i) => (
      <option key={m} value={i + 1}>
        {m}
      </option>
    ))}
  </>
);

const YearOptions = (
  <>
    {YEARS.map((y) => (
      <option key={y} value={y}>
        {y}
      </option>
    ))}
  </>
);

/**
 * Standalone "add custom expense" modal — uses the shared design-system
 * inputs (AdminInput / AdminDollarInput2 / AdminSwitch / AdminSelect) like
 * the rest of the admin. Opened from the (+) button in ExpensesManagerModal.
 */
export const AddExpenseModal = ({
  isOpen,
  onClose,
  onSubmit,
  existingExpenses,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Resolves once the expense is persisted; rejects to keep the form open. */
  onSubmit: (input: CreateCustomExpenseDto) => Promise<void>;
  /** Everything already on the list, for the near-duplicate warning. */
  existingExpenses: { id: number; name: string }[];
}) => {
  const now = new Date();
  const DEFAULT_COLOR = '#FFFFFF00';
  const [name, setName] = useState('');
  const [amountCents, setAmountCents] = useState<number>(0);
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [recurring, setRecurring] = useState(false);

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const [startMonth, setStartMonth] = useState(now.getMonth() + 1);
  const [startYear, setStartYear] = useState(now.getFullYear());
  const [hasEnd, setHasEnd] = useState(false);
  const [endMonth, setEndMonth] = useState(now.getMonth() + 1);
  const [endYear, setEndYear] = useState(now.getFullYear());

  const reset = () => {
    setName('');
    setAmountCents(0);
    setColor(DEFAULT_COLOR);
    setRecurring(false);
    setHasEnd(false);
    setMonth(now.getMonth() + 1);
    setYear(now.getFullYear());
    setStartMonth(now.getMonth() + 1);
    setStartYear(now.getFullYear());
    setEndMonth(now.getMonth() + 1);
    setEndYear(now.getFullYear());
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!name.trim()) return ErrorToast('Please enter an expense name.');
    if (!amountCents || amountCents <= 0)
      return ErrorToast('Please enter an amount greater than 0.');
    if (color === DEFAULT_COLOR)
      return ErrorToast('Please select a pie color.');

    let body: CreateCustomExpenseDto;
    if (recurring) {
      if (hasEnd) {
        const startIdx = startYear * 12 + startMonth;
        const endIdx = endYear * 12 + endMonth;
        if (endIdx < startIdx)
          return ErrorToast('End month cannot be before the start month.');
      }
      body = {
        name: name.trim(),
        amount: amountCents,
        type: 'recurring',
        startMonth,
        startYear,
        pieColor: color,
        ...(hasEnd ? { endMonth, endYear } : {}),
      };
    } else {
      // One-time expenses reuse startMonth/startYear as the month they apply.
      body = {
        name: name.trim(),
        amount: amountCents,
        type: 'oneTime',
        startMonth: month,
        startYear: year,
        pieColor: color,
      };
    }

    try {
      await onSubmit(body);
      reset();
    } catch {
      // Parent surfaced the error toast; keep the form so the admin can retry.
    }
  };

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={close}
      padding="24px"
      logo={false}
      width="560px"
      modalOverflow="auto"
      header="Add Expense"
      zIndex={140}
    >
      <Div $flex $column $gap="20px">
        <ExpenseNameField
          value={name}
          onChange={setName}
          existingExpenses={existingExpenses}
          warningSuffix="Add a second line only if this is a different cost or period."
        />

        <AdminDollarInput2
          label="Amount"
          value={amountCents}
          onValueChange={(val: number) => setAmountCents(val)}
          $textCenter
        />

        <AdminColorInput
          label="Pie Color"
          color={color}
          onChange={setColor}
          $textCenter
        />

        <Div>
          <Label label="Type" $textCenter />

          <Div $flex $gap="16px">
            <Tab
              selected={!recurring}
              onClick={() => setRecurring(false)}
              $width="calc(50% - 8px)"
            >
              One-Time
            </Tab>
            <Tab
              selected={recurring}
              onClick={() => setRecurring(true)}
              $width="calc(50% - 8px)"
            >
              Recurring
            </Tab>
          </Div>
        </Div>

        {!recurring ? (
          <Div $flex $gap="16px" $columnSM>
            <Div $width="100%">
              <AdminSelect
                label="Month"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                options={MonthOptions}
                $textCenter
              />
            </Div>
            <Div $width="100%">
              <AdminSelect
                label="Year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                options={YearOptions}
                $textCenter
              />
            </Div>
          </Div>
        ) : (
          <>
            <Div $flex $gap="16px" $columnSM>
              <Div $width="100%">
                <AdminSelect
                  label="Start Month"
                  value={startMonth}
                  onChange={(e) => setStartMonth(Number(e.target.value))}
                  options={MonthOptions}
                  $textCenter
                />
              </Div>
              <Div $width="100%">
                <AdminSelect
                  label="Start Year"
                  value={startYear}
                  onChange={(e) => setStartYear(Number(e.target.value))}
                  options={YearOptions}
                  $textCenter
                />
              </Div>
            </Div>

            <Box
              $flex
              $alignCenter
              $between
              $p="0 24px"
              $height="56px"
              style={{
                boxShadow: 'none',
                border: `1px solid ${lightTheme.mediumGray}`,
              }}
            >
              <Div>
                <H5 $m="0">Has end date</H5>
                <Disclaimer $m="0" $color={lightTheme.lightGray}>
                  Off = ongoing until cancelled
                </Disclaimer>
              </Div>
              <AdminSwitch
                checked={hasEnd}
                onChange={() => setHasEnd((p) => !p)}
              />
            </Box>

            {hasEnd && (
              <Div $flex $gap="16px" $columnSM>
                <Div $width="100%">
                  <AdminSelect
                    label="End Month"
                    value={endMonth}
                    onChange={(e) => setEndMonth(Number(e.target.value))}
                    options={MonthOptions}
                    $textCenter
                  />
                </Div>
                <Div $width="100%">
                  <AdminSelect
                    label="End Year"
                    value={endYear}
                    onChange={(e) => setEndYear(Number(e.target.value))}
                    options={YearOptions}
                    $textCenter
                  />
                </Div>
              </Div>
            )}
          </>
        )}

        <Div $flex $gap="12px" $mt="8">
          <Button $selected $gray onClick={close} $w="100%">
            Cancel
          </Button>
          <Button $selected onClick={submit} $w="100%">
            Save Expense
          </Button>
        </Div>
      </Div>
    </ModalPortal>
  );
};
